import React from 'react';
import {
  FiHeadphones,
  FiClock,
  FiList,
  FiTarget,
  FiCalendar,
  FiBookOpen,
  FiUsers,
  FiVolume2,
} from 'react-icons/fi';
import IELTSCourseTips from '../../../components/IELTSCourseTips';

export default function ListeningTips() {
  const overview = [
    { icon: <FiClock />, label: 'Time:', value: '30 minutes + 10 minutes to transfer answers' },
    { icon: <FiList />, label: 'Sections:', value: '4 sections, 40 questions' },
    { icon: <FiTarget />, label: 'Skills tested:', value: 'Understanding main ideas, details, opinions, attitudes' },
  ];

  const tips = [
    {
      title: 'Understand the Four Sections',
      content: [
        '<strong>Section 1:</strong> Conversation between two people (social context).',
        '<strong>Section 2:</strong> Monologue on an everyday topic.',
        '<strong>Section 3:</strong> Conversation in an educational context.',
        '<strong>Section 4:</strong> Academic lecture or talk.',
      ],
      highlight: 'Tip: Sections get progressively harder, so stay focused throughout.',
    },
    {
      title: 'Read Questions Before Listening',
      content: [
        'Use the time given to read questions carefully.',
        'Underline key words in the questions.',
        'Predict what type of answer is needed (number, name, place, etc.).',
      ],
      highlight: 'Tip: This preparation helps you know what to listen for.',
    },
    {
      title: 'Listen for Keywords and Synonyms',
      content: [
        'The audio often uses different words than the questions.',
        'Practice recognizing paraphrases and synonyms.',
        'Focus on content words (nouns, verbs, adjectives).',
      ],
      highlight: 'Tip: Build your vocabulary to recognize synonyms quickly.',
      linkText: 'View Vocabulary Lessons',
      linkPath: '/exams/english/#vocabulary',
      linkCategory: 'vocabulary',
    },
    {
      title: 'Take Effective Notes',
      content: [
        'Write short notes while listening.',
        'Use abbreviations to save time.',
        "Don't try to write everything — focus on answers.",
      ],
      highlight: 'Tip: Practice note-taking with podcasts and lectures.',
    },
    {
      title: 'Watch Out for Distractors',
      content: [
        'Speakers may change their minds or correct themselves.',
        'The first answer you hear may not be correct.',
        'Listen for words like "but", "however", "actually".',
      ],
      highlight: 'Tip: Wait for the speaker to finish before confirming your answer.',
    },
    {
      title: 'Pay Attention to Spelling',
      content: [
        'Wrong spelling = wrong answer.',
        'Names and places are often spelled out — listen carefully.',
        'Know the difference between similar sounds (e.g., fifteen/fifty).',
      ],
      highlight: 'Tip: Practice spelling common words and numbers.',
    },
    {
      title: 'Manage Your Focus',
      content: [
        "If you miss an answer, don't panic — move on.",
        'Stay focused for the full 30 minutes.',
        'Use the 10 minutes at the end to transfer and check answers.',
      ],
      highlight: "Tip: Don't spend too long on one question.",
    },
    {
      title: 'Practice with Different Accents',
      content: [
        'IELTS uses British, American, Australian, and other accents.',
        'Expose yourself to various English accents regularly.',
        'Watch TV shows, movies, and YouTube from different countries.',
      ],
      highlight: 'Tip: BBC, CNN, and Australian news are good practice.',
    },
  ];

  const extraStrategies = [
    'Listen actively every day — even 15 minutes helps.',
    'Practice with headphones to simulate test conditions.',
    'Review your mistakes to understand why you got them wrong.',
    'Time yourself when practicing to build test stamina.',
  ];

  const improvementTips = [
    { icon: <FiCalendar />, label: 'Daily practice:', text: 'Listen to English podcasts, news, or lectures daily.' },
    { icon: <FiVolume2 />, label: 'Active listening:', text: 'Practice listening without subtitles to improve comprehension.' },
    { icon: <FiBookOpen />, label: 'Use transcripts:', text: 'Check transcripts after listening to identify missed words.' },
    { icon: <FiUsers />, label: 'Practice tests:', text: 'Take full listening tests under timed conditions.' },
  ];

  const aiHelp = {
    title: 'Need help with IELTS Listening strategies?',
    firstMessage: 'I need help improving my IELTS Listening score. Can you help me with strategies for different question types?',
    context: 'IELTS Listening: 30 minutes + 10 minutes transfer time, 4 sections, 40 questions. Sections increase in difficulty. Tests understanding of main ideas, specific details, attitudes, and purposes of speakers. Various accents used (British, American, Australian, etc.).',
  };

  return (
    <IELTSCourseTips
      category="listening"
      icon={<FiHeadphones />}
      title="IELTS Listening: What You Should Know"
      overview={overview}
      tips={tips}
      extraStrategies={extraStrategies}
      improvementTips={improvementTips}
      aiHelp={aiHelp}
    />
  );
}
